import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import L from "leaflet";
import { Pane, useMap } from "react-leaflet";
import MarkerClusterGroup from "react-leaflet-cluster";

import { PIN_H, PIN_HEX, PIN_W, pinSvg } from "../model/constants/site-pin";
import { countBadgeHtml } from "../model/constants/count-badge";
import { PickupGeofenceLayer } from "./PickupGeofences";
import { escapeHtml } from "~/shared/lib/escape-html";
import {
  SITE_TONES,
  type SiteJob,
  type SitePin,
  type SiteTone,
} from "~/shared/lib/pickup-status/site-pins";

const PIN_SELECTED_SCALE = 1.25;
const MAX_CARD_JOBS = 3;

// The cluster ring recovers each child's tone from its icon, so icons are shared per look.
const toneByIcon = new WeakMap<L.Icon | L.DivIcon, SiteTone>();
const iconCache = new Map<string, L.DivIcon>();

const pinLook = (tone: SiteTone, selected: boolean) => {
  const scale = selected ? PIN_SELECTED_SCALE : 1;
  const [w, h] = [Math.round(PIN_W * scale), Math.round(PIN_H * scale)];
  // The card centres its pin in an even box; an odd one would put the card on a half pixel.
  const box = h + (h % 2);
  return { w, h, box, svg: pinSvg(tone, w, h, selected ? "#1f2937" : "#fff") };
};

const pinIcon = (tone: SiteTone, count: number, selected: boolean) => {
  const key = `${tone}|${count}|${selected}`;
  const cached = iconCache.get(key);
  if (cached) return cached;
  const { w, h, box, svg } = pinLook(tone, selected);
  const icon = L.divIcon({
    className: "site-pin",
    html:
      `<span style="position:relative;display:block;width:${w}px;height:${h}px;` +
      `filter:drop-shadow(0 1px 2px rgba(0,0,0,.35))">` +
      svg +
      (count > 1 ? countBadgeHtml(String(count), "top:-6px;right:-8px") : "") +
      `</span>`,
    iconSize: L.point(w, h),
    iconAnchor: L.point(w / 2, h),
    // Figma «Location Hover Tooltip»: the card's pin lands on the marker's (padding 8 + half
    // the pin; Leaflet adds a 6px margin) and it unrolls to the right from there.
    tooltipAnchor: L.point(-(8 + w / 2 + 6), box / 2 - h),
  });
  toneByIcon.set(icon, tone);
  iconCache.set(key, icon);
  return icon;
};

type Cluster = { getChildCount: () => number; getAllChildMarkers: () => L.Marker[] };

/** A ring split by each tone's share of the cluster's sites, most urgent first. */
const clusterIcon = (cluster: Cluster): L.DivIcon => {
  const counts = Object.fromEntries(SITE_TONES.map((t) => [t, 0])) as Record<SiteTone, number>;
  for (const m of cluster.getAllChildMarkers()) {
    const tone = toneByIcon.get(m.options.icon as L.DivIcon);
    if (tone) counts[tone] += 1;
  }
  const total = cluster.getChildCount();
  const size = Math.round(Math.min(72, 30 + Math.log10(total + 1) * 12));
  const centre = size / 2;
  const radius = size * 0.36;
  const stroke = size * 0.12;
  const circumference = 2 * Math.PI * radius;

  let offset = 0;
  const arcs = SITE_TONES.filter((t) => counts[t] > 0)
    .map((t) => {
      const length = (counts[t] / total) * circumference;
      const arc =
        `<circle cx="${centre}" cy="${centre}" r="${radius}" fill="none" stroke="${PIN_HEX[t]}" ` +
        `stroke-width="${stroke}" stroke-dasharray="${length} ${circumference - length}" ` +
        `stroke-dashoffset="${-offset}" transform="rotate(-90 ${centre} ${centre})"/>`;
      offset += length;
      return arc;
    })
    .join("");

  return L.divIcon({
    className: "site-cluster",
    html:
      `<svg width="${size}" height="${size}" viewBox="0 0 ${size} ${size}" ` +
      `style="filter:drop-shadow(0 1px 3px rgba(0,0,0,.15))">` +
      `<circle cx="${centre}" cy="${centre}" r="${radius + stroke / 2}" fill="#fff"/>` +
      arcs +
      `<text x="${centre}" y="${centre}" text-anchor="middle" dominant-baseline="middle" ` +
      `font-weight="bold" font-size="${Math.round(size * 0.26)}" fill="#1f2937" ` +
      `font-family="system-ui, -apple-system, sans-serif">${total}</text></svg>`,
    iconSize: L.point(size, size, true),
    iconAnchor: L.point(centre, centre),
  });
};

const CLUSTER_OPTIONS = {
  chunkedLoading: true,
  // Shrinks with zoom; from street level only pins that would overlap merge.
  maxClusterRadius: (zoom: number) => (zoom >= 15 ? PIN_W : zoom >= 14 ? 30 : zoom >= 13 ? 45 : 60),
  spiderfyOnMaxZoom: true,
  showCoverageOnHover: false,
  iconCreateFunction: clusterIcon,
} as const;

// leaflet.markercluster ships no types here; this is the part of the group used below.
type ClusterGroup = L.FeatureGroup & { addLayers: (layers: L.Layer[]) => unknown };

const CARD_CLASS =
  "!rounded-lg !border-0 !bg-black/75 !p-2 !text-white !shadow-none before:!hidden " +
  "animate-unroll-right [--unroll-from:40px] motion-reduce:animate-none";

const cardPin = (tone: SiteTone, selected: boolean) => {
  const { box, svg } = pinLook(tone, selected);
  return `<span style="display:block;height:${box}px">${svg}</span>`;
};

const cardHtml = (
  site: SitePin,
  jobLabel: (job: SiteJob) => string,
  more: string,
  selected: boolean,
) => {
  const shown = site.jobs.slice(0, MAX_CARD_JOBS);
  const lines = shown
    .map(
      (j) =>
        `<div class="text-xs font-medium leading-4" style="color:${PIN_HEX[j.tone]}">` +
        `Z${j.job.id}<span class="mx-1.5 opacity-60">|</span>${escapeHtml(jobLabel(j))}</div>`,
    )
    .join("");
  const rest = site.jobs.length - shown.length;
  return (
    `<div class="flex items-center gap-2">${cardPin(site.tone, selected)}<div class="min-w-0">` +
    `<div class="text-sm font-medium leading-5">${escapeHtml(site.name)}</div>${lines}` +
    (rest > 0
      ? `<div class="text-xs leading-4 text-white/60">${escapeHtml(more)} ${rest}</div>`
      : "") +
    `</div></div>`
  );
};

type SiteShape = {
  id: string;
  lat: number;
  lon: number;
  geometry?: unknown;
  shapeOnly: true;
  tone: SiteTone;
};

const shapeColor = (shape: SiteShape) => ({ fill: PIN_HEX[shape.tone], font: "#fff" });

type MarkerEntry = { marker: L.Marker; site: SitePin; card: (selected: boolean) => string };

// Markers go in imperatively and in one batch: thousands of <Marker> children each made the
// cluster group re-cluster on its own add/remove, which froze the page on every day switch.
export const SiteMarkersLayer = ({
  sites,
  selectedId,
  onSiteClick,
  jobLabel,
  moreLabel,
  dimmed = false,
}: {
  sites: SitePin[];
  selectedId: string | null;
  onSiteClick: (id: string) => void;
  /** The status a job's line in the hover card names. */
  jobLabel: (job: SiteJob) => string;
  /** Precedes the count of jobs the hover card has no room for. */
  moreLabel: string;
  /** The shown sites are another day's, kept while the picked day loads. */
  dimmed?: boolean;
}) => {
  const map = useMap();
  // State, not a ref: the group appears only once its pane does, after the first effects
  // ran, and cached sites never change again to run them a second time.
  const [group, setGroup] = useState<ClusterGroup | null>(null);
  const markers = useRef(new Map<string, MarkerEntry>());
  const onClickRef = useRef(onSiteClick);
  onClickRef.current = onSiteClick;
  const selectedRef = useRef(selectedId);
  selectedRef.current = selectedId;

  useEffect(() => {
    if (!group) return;
    const next = new Map<string, MarkerEntry>();
    for (const site of sites) {
      const selected = site.id === selectedRef.current;
      const card = (sel: boolean) => cardHtml(site, jobLabel, moreLabel, sel);
      const marker = L.marker([site.lat, site.lon], {
        icon: pinIcon(site.tone, site.jobs.length, selected),
        zIndexOffset: selected ? 1000 : 0,
        pane: "sites",
      })
        .bindTooltip(card(selected), {
          direction: "right",
          opacity: 1,
          className: CARD_CLASS,
          pane: "tooltipPane",
        })
        .on("click", () => onClickRef.current(site.id));
      next.set(site.id, { marker, site, card });
    }
    group.clearLayers();
    group.addLayers([...next.values()].map((m) => m.marker));
    markers.current = next;
  }, [group, sites, jobLabel, moreLabel]);

  useEffect(() => {
    if (!selectedId) return;
    const entry = markers.current.get(selectedId);
    if (!entry) return;
    const { marker, site, card } = entry;
    marker.setIcon(pinIcon(site.tone, site.jobs.length, true)).setZIndexOffset(1000);
    marker.setTooltipContent(card(true));
    return () => {
      marker.setIcon(pinIcon(site.tone, site.jobs.length, false)).setZIndexOffset(0);
      marker.setTooltipContent(card(false));
    };
  }, [selectedId, sites, group, jobLabel, moreLabel]);

  useEffect(() => {
    const pane = map.getPane("sites");
    if (!pane) return;
    pane.style.transition = "opacity 300ms";
    pane.style.opacity = dimmed ? "0.4" : "";
  }, [map, dimmed]);

  // shapeOnly: else the layer swaps each pin for its polygon at geofence zoom, losing the card.
  const shapes = useMemo(
    () =>
      sites.map(
        (site): SiteShape => ({
          id: site.id,
          lat: site.lat,
          lon: site.lon,
          geometry: site.geometry,
          shapeOnly: true,
          tone: site.tone,
        }),
      ),
    [sites],
  );
  const renderNoPin = useCallback(() => null, []);
  const shapeHandlers = useCallback(
    (shape: SiteShape) => ({ click: () => onClickRef.current(shape.id) }),
    [],
  );

  return (
    <>
      <PickupGeofenceLayer
        items={shapes}
        renderMarker={renderNoPin}
        colorFor={shapeColor}
        eventHandlersFor={shapeHandlers}
      />
      <Pane name="sites" style={{ zIndex: 450 }}>
        <MarkerClusterGroup ref={setGroup} {...CLUSTER_OPTIONS} clusterPane="sites" />
      </Pane>
    </>
  );
};
